// src/pages/LeadsBoard.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API_BASE } from "../auth/api";
import { useRealtime } from "../realtime/RealtimeProvider";

export default function LeadsBoard() {
  const navigate = useNavigate();
  const rt = useRealtime();

  // ================== STATE / REFS ==================
  const [allLeads, setAllLeads] = useState([]);
  const [loading, setLoading] = useState(false);

  const [accounts, setAccounts] = useState([]);
  const [waAccountId, setWaAccountId] = useState("");
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [draggingId, setDraggingId] = useState(null);

  const [counts, setCounts] = useState({
    open: 0,
    working: 0,
    won: 0,
    lost: 0,
    closed: 0,
    new_24h: 0,
  });

  const [snippets, setSnippets] = useState({});

  const [visibleStatuses, setVisibleStatuses] = useState({
    open: true,
    working: true,
    won: false,
    lost: false,
    closed: false,
  });
  const [showColsMenu, setShowColsMenu] = useState(false);

  const colsBtnRef = useRef(null);
  const colsMenuRef = useRef(null);

  const recentEventsRef = useRef(new Map());

  const [highlightId, setHighlightId] = useState(null);

  const leadsRef = useRef([]);
  const allLeadsRef = useRef([]);
  useEffect(() => {
    leadsRef.current = allLeads;
    allLeadsRef.current = allLeads;
  }, [allLeads]);

  const pollRef = useRef({ timer: null, failCount: 0 });

  // ================== UTILIDADES ==================
  function showToast(message) {
    rt.toast(message);
  }

  function notifyOnce(key, message) {
    const now = Date.now();
    const last = recentEventsRef.current.get(key) || 0;
    if (now - last < 1500) return;
    recentEventsRef.current.set(key, now);
    showToast(message);
  }

  function markHighlighted(id, ms = 1600) {
    if (!id) return;
    setHighlightId(id);
    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-lead-id="${id}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    setTimeout(() => setHighlightId((cur) => (cur === id ? null : cur)), ms);
  }

  function normalizePhone(p) {
    return String(p || "").replace(/[^\d+]/g, "");
  }

  function buildAvatarUrl(path) {
    if (!path) return null;
    return `${API_BASE}${path.startsWith("/") ? "" : "/"}${path}`;
  }

  function hasUnread(L) {
    return Number(L.unread_count || 0) > 0;
  }

  // update optimista al llegar mensaje
  function optimisticBumpFromMessage(payload) {
    const isInbound =
      String(payload.direction || "").toLowerCase() === "in" ||
      payload.from_me === false;

    let matchedLeadId = null;

    setAllLeads((prev) =>
      prev.map((L) => {
        const sameThread = String(L.thread_id || "") === String(payload.thread_id || "");
        const samePhone = String(L.phone || "") === String(payload.phone || "");
        if (!sameThread && !samePhone) return L;

        matchedLeadId = L.id;

        const nextUnread = isInbound
          ? Number(L.unread_count || 0) + 1
          : Number(L.unread_count || 0);

        const nextLastAt =
          payload.at || payload.timestamp || L.last_at || L.updated_at || L.created_at || null;

        const nextLastMsg =
          (payload.text || payload.body || "").trim() || L.last_message || "";

        return {
          ...L,
          unread_count: nextUnread,
          last_at: nextLastAt,
          last_message: nextLastMsg,
        };
      })
    );

    const match = (leadsRef.current || []).find(
      (L) =>
        String(L.thread_id || "") === String(payload.thread_id || "") ||
        String(L.phone || "") === String(payload.phone || "")
    );
    if (!match) return;

    const text = (payload.text || payload.body || "").trim();
    const at = payload.at || payload.timestamp || null;

    setSnippets((prev) => ({
      ...prev,
      [match.id]: { text: text || prev[match.id]?.text || "", at: at || prev[match.id]?.at || null },
    }));

    if (isInbound && matchedLeadId) {
      markHighlighted(matchedLeadId, 2200);
    }
  }

  const statuses = [
    { key: "open", title: "Open" },
    { key: "working", title: "Working" },
    { key: "won", title: "Won" },
    { key: "lost", title: "Lost" },
    { key: "closed", title: "Closed" },
  ];

  const colorFor = (st) => {
    const s = String(st || "").toLowerCase();
    if (s === "open") return "#60a5fa";
    if (s === "working") return "#f59e0b";
    if (s === "won") return "#22c55e";
    if (s === "lost") return "#ef4444";
    return "#94a3b8";
  };

  const parseDate = (s) => (s ? new Date(String(s).replace(" ", "T")) : null);
  const atStart = (s) => new Date(`${s}T00:00:00`);
  const atEnd = (s) => new Date(`${s}T23:59:59`);

  // ================== NUEVO CONTACTO ==================
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newWaAccountId, setNewWaAccountId] = useState("");
  const [newContractNumber, setNewContractNumber] = useState("");
  const [creating, setCreating] = useState(false);

  async function onCreateContactAndOpen() {
    const phone = normalizePhone(newPhone);
    if (!phone) return alert("Ingresa un teléfono válido.");

    let waId = newWaAccountId;
    if (!waId && accounts && accounts.length > 0) waId = String(accounts[0].id);
    if (!waId) return alert("No hay ninguna cuenta de WhatsApp disponible. Configura al menos una.");

    setCreating(true);
    try {
      await api.post("/contacts", {
        phone,
        name: newName || "",
        contract_number: (newContractNumber || "").trim() || null,
      });

      await api.post(`/contacts/${encodeURIComponent(phone)}/thread`, {
        name: newName && newName.trim() !== "" ? newName.trim() : null,
        created_by: null,
        wa_account_id: waId ?? null,
      });

      const waQuery = waId ? `&wa=${encodeURIComponent(waId)}` : "";
      navigate(`/chat?phone=${encodeURIComponent(phone)}${waQuery}`);

      setCreateOpen(false);
      setNewName("");
      setNewPhone("");
      setNewWaAccountId("");
      setNewContractNumber("");
    } catch (e) {
      alert(e?.response?.data?.error || "No se pudo crear el contacto/hilo");
    } finally {
      setCreating(false);
    }
  }

  // ================== FILTERS / GROUPS ==================
  const filteredLeads = useMemo(() => {
    let arr = allLeads;

    if (dateFrom || dateTo) {
      arr = arr.filter((L) => {
        const opened = parseDate(L.opened_at);
        if (!opened) return false;
        const okFrom = !dateFrom || opened >= atStart(dateFrom);
        const okTo = !dateTo || opened <= atEnd(dateTo);
        return okFrom && okTo;
      });
    }

    return [...arr].sort((a, b) => {
      const unreadDiff = Number(b.unread_count || 0) - Number(a.unread_count || 0);
      if (unreadDiff !== 0) return unreadDiff;

      const ad = new Date(a.last_at || a.updated_at || a.created_at || 0).getTime();
      const bd = new Date(b.last_at || b.updated_at || b.created_at || 0).getTime();
      return bd - ad;
    });
  }, [allLeads, dateFrom, dateTo]);

  const grouped = useMemo(() => {
    const g = { open: [], working: [], won: [], lost: [], closed: [] };
    for (const L of filteredLeads) {
      const k = (L.status || "").toLowerCase();
      if (g[k]) g[k].push(L);
    }
    return g;
  }, [filteredLeads]);

  const visibleKeys = useMemo(
    () => statuses.filter((s) => !!visibleStatuses[s.key]).map((s) => s.key),
    [visibleStatuses]
  );

  // ================== FETCHERS ==================
  async function fetchAccounts() {
    try {
      const { data } = await api.get("/wa/accounts");
      setAccounts(data.rows || []);
    } catch (e) {
      console.error(e);
    }
  }

  async function fetchLeads() {
    setLoading(true);
    try {
      const { data } = await api.get("/leads", {
        params: { q, wa_account_id: waAccountId || "", status: "" },
      });
      const newRows = data?.rows || [];

      const prevLen = allLeadsRef.current?.length || 0;
      if (prevLen > 0 && newRows.length > prevLen) {
        const diff = newRows.length - prevLen;
        showToast(`¡Entró ${diff === 1 ? "un lead nuevo" : diff + " leads nuevos"}!`);
      }

      setAllLeads(newRows);
    } catch (e) {
      console.error("fetchLeads", e);
    } finally {
      setLoading(false);
    }
  }

  async function fetchCounters() {
    try {
      const { data } = await api.get("/leads/counters");
      if (data?.counts) setCounts(data.counts);
    } catch {}
  }

  // ================== INIT ==================
  useEffect(() => {
    fetchAccounts();
    fetchLeads();
    fetchCounters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchLeads();
    fetchCounters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, waAccountId]);

  useEffect(() => {
    function onDocClick(e) {
      if (!showColsMenu) return;
      const btnOk = colsBtnRef.current && colsBtnRef.current.contains(e.target);
      const menuOk = colsMenuRef.current && colsMenuRef.current.contains(e.target);
      if (!btnOk && !menuOk) setShowColsMenu(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [showColsMenu]);

  // ================== REALTIME (WS) ==================
  useEffect(() => {
    const off = rt.onEvent(async ({ type, payload }) => {
      try {
        if (type === "new_message" && payload?.thread_id) {
          const k =
            payload.wa_message_id ||
            payload.id ||
            `${payload.thread_id}|${payload.at || payload.timestamp || ""}|${(payload.text || payload.body || "").slice(
              0,
              40
            )}`;

          notifyOnce(k, "Nuevo mensaje entrante");

          fetchCounters();
          optimisticBumpFromMessage(payload);

          await refreshLeadByThreadId(payload.thread_id, {
            preferPayloadText: payload.text || payload.body || "",
            preferAt: payload.at || payload.timestamp || null,
          });
        }

        if (type === "lead_created") {
          notifyOnce(`created|${payload.lead_id || Date.now()}`, "¡Nuevo lead creado!");
          fetchLeads();
          fetchCounters();
        }

        if (type === "lead_closed") {
          notifyOnce(`closed|${payload.lead_id || Date.now()}`, `Lead cerrado: #${payload.lead_id || ""}`);
          fetchLeads();
          fetchCounters();
        }

        if (type === "lead_updated") {
          fetchLeads();
          fetchCounters();
        }
      } catch (err) {
        console.error("Realtime LeadsBoard error:", err);
      }
    });

    return () => off();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ================== POLLING fallback ==================
  function stopPolling() {
    if (pollRef.current.timer) {
      clearInterval(pollRef.current.timer);
      pollRef.current.timer = null;
    }
  }

  function startPolling(intervalMs = 9000) {
    stopPolling();
    pollRef.current.timer = setInterval(async () => {
      try {
        await fetchCounters();
        if (document.visibilityState === "visible") {
          await fetchLeads();
        }
        pollRef.current.failCount = 0;
      } catch (e) {
        pollRef.current.failCount += 1;
        console.error("poll error", e);
      }
    }, intervalMs);
  }

  useEffect(() => {
    startPolling(9000);
    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible") {
        fetchCounters();
        fetchLeads();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ================== REFRESH SELECTIVO ==================
  async function refreshLeadByThreadId(threadId, opts = {}) {
    try {
      const { data: tdata } = await api.get(`/chat/threads/${threadId}`);
      const t = tdata?.thread;
      if (!t) return;

      const { data: ldata } = await api.get("/leads", { params: { q: t.phone } });
      const row = ldata?.rows?.[0];
      if (!row) {
        fetchLeads();
        return;
      }

      setAllLeads((prev) => prev.map((L) => (L.id === row.id ? row : L)));

      const lastText =
        (opts.preferPayloadText && String(opts.preferPayloadText).trim()) || t.last_message || "";
      const lastAt = opts.preferAt || t.last_at || t.updated_at || t.created_at || null;

      setSnippets((prev) => ({ ...prev, [row.id]: { text: lastText, at: lastAt } }));
      markHighlighted(row.id);
    } catch (err) {
      console.error("refreshLeadByThreadId", err);
      fetchLeads();
    }
  }

  // ================== HELPERS CHAT ==================
  function chatSearch(lead) {
    const phone = encodeURIComponent(lead.phone || "");
    const wa = lead.wa_account_id ? `&wa=${encodeURIComponent(lead.wa_account_id)}` : "";
    const th = lead.thread_id ? `&threadId=${encodeURIComponent(lead.thread_id)}` : "";
    const lid = lead.id ? `&leadId=${encodeURIComponent(lead.id)}` : "";
    return `?phone=${phone}${wa}${th}${lid}`;
  }

  function getChatUrl(lead) {
    return `/chat${chatSearch(lead)}`;
  }

  async function getThreadForLead(lead) {
    try {
      const { data } = await api.get("/chat/threads", {
        params: { q: lead.phone, wa_account_id: lead.wa_account_id || "" },
      });
      const rows = data?.rows || [];
      if (!rows.length) return null;
      const exact = rows.find((r) => String(r.phone) === String(lead.phone));
      return exact || rows[0];
    } catch {
      return null;
    }
  }

  async function getThreadIdForLead(lead) {
    const t = await getThreadForLead(lead);
    return t?.id || null;
  }

  async function markLeadAsRead(lead) {
    try {
      await api.post(`/leads/${lead.id}/read`);

      setAllLeads((prev) =>
        prev.map((L) =>
          L.id === lead.id
            ? { ...L, unread_count: 0 }
            : L
        )
      );
    } catch (e) {
      console.error("markLeadAsRead", e);
    }
  }

  async function gotoChat(lead) {
    setAllLeads((prev) =>
      prev.map((L) =>
        L.id === lead.id
          ? { ...L, unread_count: 0 }
          : L
      )
    );

    markLeadAsRead(lead);

    const url = getChatUrl(lead);
    navigate(url);
  }

  // snippets para visibles
  useEffect(() => {
    const targets = [...(grouped.open || []), ...(grouped.working || [])];
    const toFetch = targets.filter((L) => !snippets[L.id]);
    if (!toFetch.length) return;

    (async () => {
      for (const lead of toFetch.slice(0, 40)) {
        const t = await getThreadForLead(lead);
        setSnippets((prev) => ({
          ...prev,
          [lead.id]: {
            text: t?.last_message || "",
            at: t?.last_at || t?.updated_at || t?.created_at || null,
          },
        }));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grouped.open, grouped.working]);

  // ================== MOVER LEAD ==================
  async function moveLead(lead, destStatus) {
    const to = String(destStatus).toLowerCase();
    try {
      if (to === "working") {
        const threadId = await getThreadIdForLead(lead);
        if (!threadId) return alert("No se encontró el hilo asociado para este lead.");
        await api.post(`/chat/threads/${threadId}/lead/start`);
      } else if (to === "won" || to === "lost" || to === "closed") {
        const threadId = await getThreadIdForLead(lead);
        if (!threadId) return alert("No se encontró el hilo asociado para este lead.");
        await api.post(`/chat/threads/${threadId}/lead/close`, { outcome: to });
      } else if (to === "open") {
        await api.patch(`/leads/${lead.id}`, { status: "open" });
      } else {
        return;
      }
      await fetchLeads();
      await fetchCounters();
    } catch (e) {
      alert(e?.response?.data?.error || "No se pudo mover el lead");
    }
  }

  // ================== DnD ==================
  function onDragStart(e, lead) {
    e.dataTransfer.setData("text/plain", String(lead.id));
    setDraggingId(lead.id);
  }

  function onDragOver(e) {
    e.preventDefault();
  }

  function onDrop(e, destKey) {
    e.preventDefault();
    const id = Number(e.dataTransfer.getData("text/plain"));
    const lead = filteredLeads.find((L) => Number(L.id) === id);
    setDraggingId(null);
    if (!lead) return;
    if (String(lead.status).toLowerCase() === String(destKey)) return;
    moveLead(lead, destKey);
  }

  function onDragEnd() {
    setDraggingId(null);
  }

  function toggleStatusVisible(key) {
    setVisibleStatuses((v) => ({ ...v, [key]: !v[key] }));
  }

  // ================== UI ==================
  return (
    <div className="leads-root">
      <style>{css}</style>

      <div className="topbar">
        <div className="topbar-row">
          <div className="search">
            <svg viewBox="0 0 24 24" className="icon">
              <path
                d="M21 21l-4.35-4.35M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15z"
                stroke="currentColor"
                strokeWidth="1.7"
                fill="none"
                strokeLinecap="round"
              />
            </svg>
            <input
              className="input"
              placeholder="Buscar por nombre, notas o teléfono…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>

          <div className="dates">
            <input className="input" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            <span className="arrow">→</span>
            <input className="input" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>

          <div className="select-wrap">
            <select className="input select" value={waAccountId} onChange={(e) => setWaAccountId(e.target.value)}>
              <option value="">Todas las cuentas</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.label} ({a.provider})
                </option>
              ))}
            </select>
            <svg className="caret" viewBox="0 0 24 24">
              <path d="M7 10l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
            </svg>
          </div>
        </div>

        <div className="topbar-row">
          <div className="cols-menu-wrap">
            <button ref={colsBtnRef} className="input toggle" onClick={() => setShowColsMenu((v) => !v)}>
              Columnas
            </button>

            {showColsMenu && (
              <div ref={colsMenuRef} className="cols-menu">
                {statuses.map((s) => {
                  const on = !!visibleStatuses[s.key];
                  return (
                    <button
                      key={s.key}
                      type="button"
                      className={`cols-item ${on ? "on" : ""}`}
                      onClick={() => toggleStatusVisible(s.key)}
                    >
                      <span className={`checkbox ${on ? "on" : ""}`}>{on ? "✓" : ""}</span>
                      <span className="dot" style={{ background: colorFor(s.key) }} />
                      <span className="title">{s.title}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="counters">
            <span className="pill open">
              Open <b>{counts.open || 0}</b>
            </span>
            <span className="pill working">
              Working <b>{counts.working || 0}</b>
            </span>
            <span className="pill won">
              Won <b>{counts.won || 0}</b>
            </span>
            <span className="pill lost">
              Lost <b>{counts.lost || 0}</b>
            </span>
            <span className="pill closed">
              Closed <b>{counts.closed || 0}</b>
            </span>
          </div>

          <button className="btn-new" onClick={() => setCreateOpen(true)}>
            + Nuevo
          </button>
        </div>
      </div>

      <div
        className="board"
        style={{ gridTemplateColumns: `repeat(${Math.max(1, visibleKeys.length)}, minmax(260px, 1fr))` }}
      >
        {statuses
          .filter((col) => visibleStatuses[col.key])
          .map((col) => (
            <div key={col.key} className="column" onDragOver={onDragOver} onDrop={(e) => onDrop(e, col.key)}>
              <div className="col-header">
                <span className="dot" style={{ background: colorFor(col.key) }} />
                {col.title}
                <span className="qty">{grouped[col.key]?.length || 0}</span>
              </div>

              {loading && !filteredLeads.length ? (
                <div className="empty">Cargando…</div>
              ) : grouped[col.key]?.length ? (
                grouped[col.key].map((L) => (
                  <div
                    key={L.id}
                    data-lead-id={L.id}
                    className={[
                      "card",
                      highlightId === L.id ? "card-updated" : "",
                      hasUnread(L) ? "card-unread" : "",
                    ].join(" ")}
                    onClick={() => gotoChat(L)}
                    title={`Lead #${L.id} · ${L.status || ""}`}
                    role="button"
                    tabIndex={0}
                  >
                    {Number(L.unread_count) > 0 && (
                      <div className="lead-unread-badge">
                        {Number(L.unread_count) > 99 ? "99+" : Number(L.unread_count)}
                      </div>
                    )}

                    <div className="card-top">
                      <button
                        className="grab"
                        title="Arrastrar"
                        draggable
                        onDragStart={(e) => onDragStart(e, L)}
                        onDragEnd={onDragEnd}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <svg viewBox="0 0 24 24">
                          <path
                            d="M7 10h2M7 14h2M11 10h2M11 14h2M15 10h2M15 14h2"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>

                      <div className="card-title">
                        {L.avatar_path ? (
                          <img src={buildAvatarUrl(L.avatar_path)} className="avatar" alt="avatar" />
                        ) : (
                          <div className="avatar ph">{(L.name || L.phone || "?")[0]}</div>
                        )}

                        <div className="name-row">
                          <span className="name">{L.name || L.phone}</span>
                          {hasUnread(L) ? <span className="new-dot" /> : null}
                        </div>

                        <span className="status" style={{ background: colorFor(L.status) }}>
                          {L.status}
                        </span>
                      </div>
                    </div>

                    <div className="muted small">{L.phone}</div>

                    {["open", "working"].includes(String(L.status || "").toLowerCase()) && (
                      <div className="snippet">
                        <span className="label">Último:</span>
                        <span className="clip">
                          {(snippets[L.id]?.text || "—").slice(0, 80)}
                          {snippets[L.id]?.text && snippets[L.id].text.length > 80 ? "…" : ""}
                        </span>
                      </div>
                    )}

                    {L.notes ? <div className="notes">{L.notes}</div> : null}

                    <div className="actions">
                      <button
                        className="mini"
                        title="Abrir chat"
                        onClick={(e) => {
                          e.stopPropagation();
                          gotoChat(L);
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        draggable={false}
                      >
                        <svg viewBox="0 0 24 24">
                          <path
                            d="M21 15a4 4 0 0 1-4 4H8l-5 4V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4v8z"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.6"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>

                      <button
                        className="mini ok"
                        title="Marcar Won"
                        onClick={(e) => {
                          e.stopPropagation();
                          moveLead(L, "won");
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        draggable={false}
                      >
                        <svg viewBox="0 0 24 24">
                          <path d="M5 13l4 4L19 7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      </button>

                      <button
                        className="mini warn"
                        title="Marcar Lost"
                        onClick={(e) => {
                          e.stopPropagation();
                          moveLead(L, "lost");
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        draggable={false}
                      >
                        <svg viewBox="0 0 24 24">
                          <path d="M6 6l12 12M18 6l-12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty">Sin leads aquí</div>
              )}
            </div>
          ))}
      </div>

      {createOpen && (
        <div className="nb-modal-bg">
          <div className="nb-modal" role="dialog" aria-modal="true">
            <div className="nb-modal-head">
              <div className="nb-modal-title">
                <span className="nb-badge">Nuevo</span>
                <div>
                  <div className="nb-h1">Crear contacto</div>
                  <div className="nb-sub">Registra el cliente y abre el chat en la cuenta seleccionada.</div>
                </div>
              </div>

              <button
                className="nb-x"
                onClick={() => {
                  if (!creating) setCreateOpen(false);
                }}
                disabled={creating}
                title="Cerrar"
                type="button"
              >
                ✕
              </button>
            </div>

            <div className="nb-modal-body">
              <div className="nb-grid">
                <label className="nb-field">
                  <span className="nb-label">Nombre</span>
                  <input
                    className="nb-input"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="Ej: Juan Pérez"
                    autoFocus
                  />
                </label>

                <label className="nb-field">
                  <span className="nb-label">Teléfono *</span>
                  <input
                    className="nb-input"
                    value={newPhone}
                    onChange={(e) => setNewPhone(e.target.value)}
                    placeholder="Ej: 5939xxxxxxxx"
                    inputMode="tel"
                  />
                  <span className="nb-help">Solo números (E.164 recomendado). Ej: 5939xxxxxxx</span>
                </label>

                <label className="nb-field">
                  <span className="nb-label">Número de contrato</span>
                  <input
                    className="nb-input"
                    value={newContractNumber}
                    onChange={(e) => setNewContractNumber(e.target.value)}
                    placeholder="Ej: 001-2026-XYZ"
                  />
                </label>

                <label className="nb-field">
                  <span className="nb-label">Cuenta de WhatsApp</span>
                  <div className="nb-select-wrap">
                    <select className="nb-input nb-select" value={newWaAccountId} onChange={(e) => setNewWaAccountId(e.target.value)}>
                      <option value="">Autoseleccionar</option>
                      {accounts.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.label} ({a.provider})
                        </option>
                      ))}
                    </select>
                    <span className="nb-caret">▾</span>
                  </div>
                  <span className="nb-help">Si no eliges una, se usará la primera cuenta disponible.</span>
                </label>
              </div>
            </div>

            <div className="nb-modal-foot">
              <button
                className="nb-btn nb-btn-ghost"
                onClick={() => {
                  if (!creating) setCreateOpen(false);
                }}
                disabled={creating}
                type="button"
              >
                Cancelar
              </button>

              <button className="nb-btn nb-btn-primary" onClick={onCreateContactAndOpen} disabled={creating} type="button">
                {creating ? "Creando..." : "Crear y abrir chat"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------- CSS in-file -------------------- */
const css = `
.leads-root{ height: calc(100vh - 80px); padding: 10px; display:grid; grid-template-rows:auto 1fr; gap:10px; color:#e5e7eb; background:#0b1220; }
.input{ background: rgba(255,255,255,.04); border:1px solid rgba(255,255,255,.08); border-radius:10px; padding:8px 12px; color:#e5e7eb; }
.input{ height: 40px; display: flex; align-items: center; }
.input:focus{ outline:none; border-color: rgba(59,130,246,.45); box-shadow:0 0 0 3px rgba(59,130,246,.18); }

.topbar{ display:flex; flex-direction:column; gap:10px; }
.topbar-row{ display:flex; gap:10px; align-items:center; flex-wrap:wrap; }

.search{ position: relative; flex: 1; min-width: 280px; display: flex; align-items: center; }
.select-wrap{ min-width:220px; }

.btn-new{ background:#3b82f6; border:1px solid #2563eb; color:white; padding:8px 16px; border-radius:10px; font-weight:700; cursor:pointer; white-space:nowrap; }
.btn-new:hover{ background:#2563eb; }

.search .icon{ position:absolute; left:12px; top:50%; transform:translateY(-50%); width:16px; height:16px; color:#94a3b8; pointer-events:none; }
.search .input{ width: 100%; height: 40px; padding-left: 40px; padding-right: 12px; line-height: 40px; }

.dates{ display:flex; gap:6px; align-items:center; }
.dates .arrow{ color:#94a3b8; font-size:12px; }

.select-wrap{ position:relative; }
.select{ appearance:none; padding-right:28px; }
.caret{ position:absolute; right:8px; top:50%; transform:translateY(-50%); width:16px; height:16px; color:#94a3b8; }

.cols-menu-wrap{ position:relative; display:inline-block; }
.toggle{ cursor:pointer; }
.cols-menu{
  position:absolute; top: calc(100% + 6px); left: 0;
  background:#0f172a; border:1px solid rgba(255,255,255,.12); border-radius:12px; padding:10px; display:grid; gap:8px; z-index:10000; min-width:220px; box-shadow:0 12px 30px rgba(0,0,0,.45);
}
.cols-item{ display:flex; align-items:center; gap:10px; width:100%; padding:8px 10px; border:none; background:transparent; color:#e5e7eb; border-radius:10px; cursor:pointer; text-align:left; }
.cols-item:hover{ background: rgba(255,255,255,.06); }
.cols-item.on{ background: rgba(59,130,246,.12); }

.checkbox{ width:18px; height:18px; border-radius:6px; display:grid; place-items:center; border:2px solid #3b82f6; font-weight:900; font-size:12px; line-height:1; color:#0b1220; background:transparent; }
.checkbox.on{ background:#3b82f6; }

.counters{ display:flex; gap:8px; margin-left:auto; align-items:center; }
.pill{ border:1px solid rgba(255,255,255,.08); background: rgba(255,255,255,.04); padding:6px 10px; border-radius:999px; color:#cfd8ef; font-size:12px; }
.pill.open{ border-color:#60a5fa33; } .pill.working{ border-color:#f59e0b33; } .pill.won{ border-color:#22c55e33; } .pill.lost{ border-color:#ef444433; } .pill.closed{ border-color:#94a3b833; }

.board{ display:grid; gap:10px; overflow:auto; }
.column{ background:#0f172a; border:1px solid rgba(255,255,255,.08); border-radius:12px; padding:10px; min-width: 260px; }
.col-header{ display:flex; align-items:center; gap:8px; font-weight:700; margin-bottom:8px; }
.dot{ width:10px; height:10px; border-radius:999px; display:inline-block; }
.qty{ margin-left:auto; color:#94a3b8; font-size:12px; }

.card{
  background:#111827;
  border:1px solid #1e293b;
  border-radius:12px;
  padding:10px;
  margin:8px 0;
  cursor:pointer;
  position:relative;
  transition: border-color .2s ease, box-shadow .2s ease, background .2s ease;
}

.card-unread{
  border-color: rgba(59,130,246,.45);
  box-shadow:
    0 0 0 1px rgba(59,130,246,.18),
    0 8px 24px rgba(37,99,235,.12);
  background: linear-gradient(180deg, rgba(30,41,59,.95), rgba(17,24,39,1));
}

.card-unread::before{
  content:"";
  position:absolute;
  left:0;
  top:10px;
  bottom:10px;
  width:4px;
  border-radius:999px;
  background:#3b82f6;
}

.card-top{ display:flex; align-items:center; gap:8px; }
.grab{ width:28px; height:28px; border-radius:8px; border:1px solid rgba(255,255,255,.08); background: rgba(255,255,255,.04); color:#cbd5e1; display:grid; place-items:center; cursor:grab; flex:0 0 auto; }
.grab:active{ cursor:grabbing; }
.grab svg{ width:16px; height:16px; }

.card-title{ display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
.card-title .name{ font-weight:700; color:#e7eaf3; }

.name-row{
  display:flex;
  align-items:center;
  gap:6px;
}

.new-dot{
  width:8px;
  height:8px;
  border-radius:999px;
  background:#22c55e;
  box-shadow:0 0 10px rgba(34,197,94,.55);
  flex:none;
}

.status{ font-size:10px; color:#0b1220; font-weight:800; padding:2px 8px; border-radius:999px; text-transform:uppercase; }

.small{ font-size:12px; }
.muted{ color:#94a3b8; }
.notes{ margin-top:6px; color:#d1d5db; font-size:12px; }

.snippet{ margin-top:6px; display:flex; gap:6px; align-items:flex-start; }
.snippet .label{ color:#94a3b8; font-size:12px; flex:0 0 auto; }
.snippet .clip{ color:#d1d5db; font-size:12px; }

.actions{ display:flex; gap:6px; margin-top:8px; }
.mini{ width:32px; height:32px; border-radius:10px; border:1px solid rgba(255,255,255,.08); background: rgba(255,255,255,.04); color:#cbd5e1; display:grid; place-items:center; }
.mini svg{ width:16px; height:16px; }
.mini.ok{ color:#22c55e; } .mini.warn{ color:#ef4444; }

.empty{ color:#94a3b8; font-size:12px; padding:10px; text-align:center; }

@media (max-width: 1200px){ .board{ grid-template-columns: repeat(1, minmax(260px, 1fr));} }

.avatar{ width:32px; height:32px; border-radius:999px; object-fit:cover; border:1px solid rgba(255,255,255,.15); }
.avatar.ph{ background:#1e293b; color:#e5e7eb; font-weight:800; display:grid; place-items:center; font-size:14px; }

.lead-unread-badge {
  position: absolute;
  top: 6px;
  right: 6px;
  background: #ef4444;
  color: white;
  font-size: 11px;
  font-weight: 800;
  padding: 3px 7px;
  border-radius: 999px;
  min-width: 22px;
  text-align: center;
  border: 1px solid rgba(255,255,255,.3);
  box-shadow: 0 2px 6px rgba(0,0,0,.35);
  animation: unreadPulse 1.4s infinite;
}

@keyframes unreadPulse {
  0%   { transform: scale(1); box-shadow: 0 0 0 0 rgba(239,68,68,.45); }
  70%  { transform: scale(1.04); box-shadow: 0 0 0 10px rgba(239,68,68,0); }
  100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(239,68,68,0); }
}

@keyframes card-flash {
  0%   { background: #0f172a; box-shadow: 0 0 0 rgba(59,130,246,0); }
  20%  { background: #0b265f; box-shadow: 0 0 0 rgba(59,130,246,0.4); }
  50%  { background: #0f172a; box-shadow: 0 0 18px rgba(59,130,246,0.35); }
  100% { background: #111827; box-shadow: 0 0 0 rgba(59,130,246,0); }
}
.card-updated{
  position: relative;
  animation: card-flash 1.2s ease-out;
  outline: 2px solid rgba(59,130,246,.55);
  outline-offset: 0;
  transition: outline-color .6s ease, background .6s ease, box-shadow .6s ease;
}

.nb-modal-bg{
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.58);
  display:flex;
  align-items:center;
  justify-content:center;
  z-index: 99999;
  padding: 18px;
}

.nb-modal{
  width: 520px;
  max-width: 94vw;
  background: #0f172a;
  border: 1px solid rgba(255,255,255,.10);
  border-radius: 16px;
  box-shadow: 0 18px 55px rgba(0,0,0,.55);
  overflow: hidden;
}

.nb-modal-head{
  display:flex;
  align-items:flex-start;
  justify-content:space-between;
  padding: 14px 14px 10px 14px;
  border-bottom: 1px solid rgba(255,255,255,.08);
  background: linear-gradient(180deg, rgba(59,130,246,.10), rgba(15,23,42,0));
}

.nb-modal-title{ display:flex; gap: 10px; align-items:flex-start; }

.nb-badge{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  height: 24px;
  padding: 0 10px;
  border-radius: 999px;
  background: rgba(59,130,246,.18);
  border: 1px solid rgba(59,130,246,.35);
  color: #cfe0ff;
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
}

.nb-h1{ font-size: 16px; font-weight: 900; color: #e5e7eb; line-height: 1.2; }
.nb-sub{ font-size: 12px; color: #94a3b8; margin-top: 3px; }

.nb-x{
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,.10);
  background: rgba(255,255,255,.04);
  color: #cbd5e1;
  cursor: pointer;
}
.nb-x:hover{ background: rgba(255,255,255,.07); }
.nb-x:disabled{ opacity:.5; cursor:not-allowed; }

.nb-modal-body{ padding: 14px; }

.nb-grid{ display:grid; grid-template-columns: 1fr 1fr; gap: 12px; }
@media (max-width: 560px){ .nb-grid{ grid-template-columns: 1fr; } }

.nb-field{ display:grid; gap: 6px; }
.nb-label{ font-size: 12px; font-weight: 800; color: #cbd5e1; }
.nb-help{ font-size: 11px; color: #94a3b8; }

.nb-input{
  height: 42px;
  border-radius: 12px;
  border: 1px solid rgba(255,255,255,.10);
  background: rgba(255,255,255,.04);
  color: #e5e7eb;
  padding: 0 12px;
  outline: none;
  box-sizing: border-box;
}
.nb-input::placeholder{ color: rgba(148,163,184,.85); }
.nb-input:focus{
  border-color: rgba(59,130,246,.55);
  box-shadow: 0 0 0 3px rgba(59,130,246,.18);
  background: rgba(255,255,255,.06);
}

.nb-select-wrap{ position:relative; }
.nb-select{ appearance: none; padding-right: 34px; }
.nb-caret{
  position:absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  pointer-events:none;
}

.nb-modal-foot{
  display:flex;
  justify-content:flex-end;
  gap: 10px;
  padding: 12px 14px 14px 14px;
  border-top: 1px solid rgba(255,255,255,.08);
  background: rgba(2,6,23,.35);
}

.nb-btn{
  height: 40px;
  border-radius: 12px;
  padding: 0 14px;
  border: 1px solid rgba(255,255,255,.10);
  cursor: pointer;
  font-weight: 900;
  color: #e5e7eb;
  background: rgba(255,255,255,.05);
}
.nb-btn:disabled{ opacity:.55; cursor:not-allowed; }
.nb-btn-ghost:hover{ background: rgba(255,255,255,.08); }

.nb-btn-primary{
  border-color: rgba(59,130,246,.55);
  background: linear-gradient(180deg, rgba(59,130,246,.95), rgba(37,99,235,.95));
}
.nb-btn-primary:hover{ filter: brightness(1.05); }
`;
